import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGifts } from '@fortawesome/free-solid-svg-icons';
import './DictaBookShelfList.css';

const DictaBookShelf = () => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();

  const [myBooks, setMyBooks] = useState([]);
  const [fetchData] = useFetch();

  const fetchMyBooks = useCallback(() => {
    const config = {
      url: "/mybooks/dicta",
      method: "get",
      headers: { Authorization: authState.token },
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        if (response && Array.isArray(response.mybooks)) {
          setMyBooks(response.mybooks);
        } else {
          console.error("Invalid response format: Book not found in response");
        }
      })
      .catch(error => {
        console.error("Error fetching MyBooks:", error);
      });
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchMyBooks();
  }, [authState.isLoggedIn, fetchMyBooks]);

  const handleReadClick = (bookCode, bookMasterId, bookCollectionName, lastPageRead, bookPages, bookIsChildren) => {
    navigate('/read-dicta-book', {
      state: {
        bookId: bookCode,
        bookMasterId,
        bookCollectionName,
        lastPageRead,
        bookPages,
        isChildren: bookIsChildren
      }
    });
  };

  const sortedBooks = myBooks.sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));

  return (
    <div className="image-container px-4 py-8">
      {myBooks.length === 0 ? (
        <div className="flex justify-center w-full h-20 text-white bg-gray-500 rounded-md shadow-lg">
          <p className='text-center text-lg sm:text-2xl'>📚 {authState.user.name}'s DictaBookshelf is Empty!!</p>
        </div>
      ) : (
        <div className="book-grid gap-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {sortedBooks.map((book, index) => (
            <div key={book.bookCode} className="book-item p-4 bg-gray-800 text-white rounded-lg shadow-lg">
              <div className="flex flex-col items-center">
                <p className='text-base sm:text-lg font-semibold mb-4 text-black'>#{index + 1}</p>
                <button 
                  onClick={() => handleReadClick(
                    book.bookCode,
                    book.bookId,
                    book.bookCollectionName,
                    book.lastPageRead,
                    book.bookPages,
                    book.isChildren
                  )}
                  className="read-button px-4 py-2 sm:px-6 sm:py-4 rounded-full text-base sm:text-xl font-bold text-white transition duration-300 transform hover:scale-105"
                  style={{
                    backgroundImage: 'linear-gradient(135deg, #6b73ff 0%, #000dff 74%)',
                    boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  Read {book.bookTitle}
                </button>
              </div>
              <div className="book-info mt-4 sm:mt-6 bg-gray-700 p-4 sm:p-6 rounded-md">
                <div dangerouslySetInnerHTML={{ __html: book.bookHighlight }} className="mb-2 text-white text-sm sm:text-base" />
              </div>
              <div className="book-info mt-4 sm:mt-6 bg-gray-700 p-4 sm:p-6 rounded-md">
                <p className='text-yellow-400 text-sm sm:text-base'>Pages: {book.bookPages}</p>
                <p className='text-yellow-400 text-sm sm:text-base'>Owned since: {new Date(book.purchaseDate).toLocaleDateString()}</p>
                <p className='text-pink-400 text-sm sm:text-base'>Last Page Read: {book.lastPageRead}</p>
              </div>

              <div className="book-info mt-4 sm:mt-6 bg-gray-700 p-4 sm:p-6 rounded-md">
                <p className='text-indigo-300 text-sm sm:text-base ml-2'>Subject: {book.bookSubject}</p>
                <p className='text-indigo-300 text-sm sm:text-base ml-2'>Category: {book.bookCategory}</p>
                <p className='text-indigo-300 text-sm sm:text-base ml-2'>Sub-Category: {book.bookSubCategory}</p>
              </div>

              <div className="book-info mt-4 sm:mt-6 bg-gray-700 p-4 sm:p-6 rounded-md">
                <p className='text-gray-300 italic text-xs sm:text-sm'>{book.remarks}</p>
              </div>

              <div className="book-info mt-4 sm:mt-6 bg-gray-700 p-4 sm:p-6 rounded-md">
                <p className='text-green-300 font-semibold text-sm sm:text-base'>Author: {book.bookAuthor}</p>
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DictaBookShelf;
